import { useUser } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import Joyride from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

import EmptyAvatar from '@/assets/icons/empty-avatar.svg?react';
import UnsavedChangesDialog from '@/components/dialogs/UnsavedChangesDialog';
import { CustomTooltip } from '@/components/tour/CustomTooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { profileSteps } from '@/constants/tour/profile-steps';
import { useTourManager } from '@/hooks/tour/useTourManager';

import { IndustriesTab } from './IndustriesTab';
import { MyProfileTab } from './MyProfileTab';

type TabValue = 'general' | 'industries';

const TAB_OPTIONS: { value: TabValue; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'industries', label: 'Industries' },
];

const DEFAULT_TAB: TabValue = 'general';

export const DashboardProfile = () => {
  const [selectedTab, setSelectedTab] = useState<TabValue>(DEFAULT_TAB);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<TabValue | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [searchParams] = useSearchParams();
  const tour = searchParams.get('tour');

  // custom hooks
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTour } = useTourManager();

  const isAddressValid =
    (user?.publicMetadata?.address &&
      user?.publicMetadata?.city &&
      user?.publicMetadata?.state &&
      user?.publicMetadata?.zip_code) ||
    false;

  // event handlers
  const handleClickTab = (value: TabValue): void => {
    if (isDirty) {
      setPendingTab(value);
      setShowConfirmDialog(true);
      return;
    }
    setSelectedTab(value);
    navigate(`/dashboard/profile?tab=${value}`);
  };

  const handleConfirmTabSwitch = async (): Promise<void> => {
    setShowConfirmDialog(false);
    // Discard changes: do not save, just navigate
    if (pendingTab) {
      setIsDirty(false);
      setSelectedTab(pendingTab);
      navigate(`/dashboard/profile?tab=${pendingTab}`);
      setPendingTab(null);
    }
  };

  const handleCancelTabSwitch = (): void => {
    setShowConfirmDialog(false);
  };

  const renderUserAvatar = () => {
    if (user?.imageUrl) {
      return (
        <img
          src={user.imageUrl}
          alt={user.fullName || 'User avatar'}
          className="h-[90px] w-[90px] rounded-full border-2 border-white shadow-lg"
        />
      );
    }
    return (
      <EmptyAvatar className="h-[90px] w-[90px] rounded-full border-2 border-white shadow-lg" />
    );
  };

  // effects
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const newDefaultTab = (queryParams.get('tab') as TabValue) || DEFAULT_TAB;
    setSelectedTab(newDefaultTab);
  }, [location]);

  useEffect(() => {
    if (tour === 'true') {
      setTimeout(() => {
        currentTour?.startTour();
      }, 1000);
    }
  }, [tour]);

  return (
    <div className="space-y-4">
      <Joyride
        steps={profileSteps}
        run={currentTour?.run || false}
        stepIndex={currentTour?.stepIndex || 0}
        callback={currentTour?.handleJoyrideCallback || (() => {})}
        continuous={true}
        showProgress={false}
        showSkipButton={false}
        disableOverlayClose={false}
        disableCloseOnEsc={false}
        hideCloseButton={false}
        tooltipComponent={CustomTooltip}
        floaterProps={{
          disableAnimation: true,
        }}
        styles={{
          options: {
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            primaryColor: '#3b82f6',
            textColor: '#333',
            zIndex: 1000,
          },
        }}
      />
      <div className="flex flex-col justify-center md:flex-row md:justify-between">
        <div className="tour-profile-header flex flex-col items-center gap-5 md:flex-row">
          {renderUserAvatar()}
          <div className="space-y-1 max-md:text-center">
            <h2 className="as-strong">{user?.fullName || '####'}</h2>
            <p className="as-body-02 as-strong text-muted-foreground">
              {isAddressValid &&
                `${user?.publicMetadata?.address}, ${user?.publicMetadata?.city}, ${user?.publicMetadata?.state}, ${user?.publicMetadata?.zip_code}`}
              {!isAddressValid && '####'}
            </p>
          </div>
        </div>
      </div>
      <Tabs
        value={selectedTab}
        onValueChange={(value) => handleClickTab(value as TabValue)}
      >
        <TabsList className="mb-4 w-full md:w-fit">
          {TAB_OPTIONS.map(({ value, label }) => (
            <TabsTrigger
              key={value}
              value={value}
              className={`w-full tour-${value}-tab`}
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value="general"
          className="rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <MyProfileTab setIsDirty={setIsDirty} />
        </TabsContent>
        <TabsContent
          value="industries"
          className="rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <IndustriesTab setIsDirty={setIsDirty} />
        </TabsContent>
      </Tabs>

      <UnsavedChangesDialog
        open={showConfirmDialog}
        onConfirm={handleConfirmTabSwitch}
        onCancel={handleCancelTabSwitch}
      />
    </div>
  );
};
